import { decodeFrameHeader, FRAME_HEADER_SIZE, FrameType, type FrameHeader } from '@relay/shared';

export interface SplitH264 {
  header: FrameHeader;
  payload: Uint8Array;
}

export function splitFrame(buf: ArrayBuffer): SplitH264 {
  if (buf.byteLength < FRAME_HEADER_SIZE) {
    throw new Error(`frame too small: ${buf.byteLength}`);
  }
  const headerBytes = new Uint8Array(buf, 0, FRAME_HEADER_SIZE);
  const payload = new Uint8Array(buf, FRAME_HEADER_SIZE, buf.byteLength - FRAME_HEADER_SIZE);
  return { header: decodeFrameHeader(headerBytes), payload };
}

export const webCodecsSupported = typeof VideoDecoder !== 'undefined' && typeof EncodedVideoChunk !== 'undefined';

const NAL_TYPE_SPS = 7;
// Baseline 3.1 - works for libx264 ultrafast output up to 1920x1080.
// Used when SPS extraction fails for any reason.
export const FALLBACK_CODEC_STRING = 'avc1.42E01F';

function toHex2(n: number): string {
  return n.toString(16).toUpperCase().padStart(2, '0');
}

/**
 * Walk the Annex-B stream in `payload` looking for an SPS NAL (type 7). If
 * found, return the WebCodecs codec string built from its profile_idc,
 * constraint_set flags, and level_idc. Otherwise return the fallback.
 *
 * The three bytes we read (profile_idc, constraint flags, level_idc) sit
 * immediately after the NAL header byte and are below 0x03, so no
 * emulation-prevention-byte unescaping is needed.
 */
export function extractCodecString(payload: Uint8Array): string {
  for (let i = 0; i + 4 < payload.byteLength; i++) {
    const sc3 = payload[i] === 0 && payload[i + 1] === 0 && payload[i + 2] === 1;
    const sc4 = payload[i] === 0 && payload[i + 1] === 0 && payload[i + 2] === 0 && payload[i + 3] === 1;
    if (!sc3 && !sc4) continue;
    const headerIdx = i + (sc3 ? 3 : 4);
    const nalHeader = payload[headerIdx];
    if (nalHeader === undefined) continue;
    const nalType = nalHeader & 0x1f;
    if (nalType !== NAL_TYPE_SPS) {
      // Skip past this start code to avoid re-matching it.
      i = headerIdx;
      continue;
    }
    const profileIdc = payload[headerIdx + 1];
    const constraintFlags = payload[headerIdx + 2];
    const levelIdc = payload[headerIdx + 3];
    if (profileIdc === undefined || constraintFlags === undefined || levelIdc === undefined) {
      return FALLBACK_CODEC_STRING;
    }
    return `avc1.${toHex2(profileIdc)}${toHex2(constraintFlags)}${toHex2(levelIdc)}`;
  }
  return FALLBACK_CODEC_STRING;
}

export interface H264RenderTarget {
  draw: (frame: VideoFrame) => void;
}

export interface H264DecodeStats {
  fps: number;
  framesDecoded: number;
  framesDropped: number;
}

export interface H264FrameRouter {
  ingest: (buf: ArrayBuffer) => Promise<void>;
  stats: () => H264DecodeStats;
  reset: () => void;
  close: () => void;
}

export function makeCanvasH264Target(canvas: HTMLCanvasElement): H264RenderTarget {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  return {
    draw(frame: VideoFrame) {
      if (canvas.width !== frame.displayWidth) canvas.width = frame.displayWidth;
      if (canvas.height !== frame.displayHeight) canvas.height = frame.displayHeight;
      ctx.drawImage(frame, 0, 0);
      frame.close();
    },
  };
}

export function makeH264Router(target: H264RenderTarget): H264FrameRouter {
  if (!webCodecsSupported) {
    throw new Error('WebCodecs not supported in this browser');
  }
  let framesDecoded = 0;
  let framesDropped = 0;
  let fpsWindow: number[] = [];
  let configured = false;
  let lastSeq = -1;

  const decoder = new VideoDecoder({
    output: (frame: VideoFrame) => {
      try {
        target.draw(frame);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[h264-decoder] draw error', err);
        try { frame.close(); } catch { /* */ }
      }
      framesDecoded++;
      const now = performance.now();
      fpsWindow.push(now);
      fpsWindow = fpsWindow.filter((t) => now - t <= 1000);
    },
    error: (err) => {
      // eslint-disable-next-line no-console
      console.error('[h264-decoder] decode error', err);
    },
  });

  return {
    async ingest(buf: ArrayBuffer): Promise<void> {
      const { header, payload } = splitFrame(buf);
      if (lastSeq >= 0 && header.seq <= lastSeq) {
        framesDropped++;
        return;
      }
      lastSeq = header.seq;

      const isKey = header.frameType === FrameType.VIDEO_KEYFRAME;
      if (!configured) {
        // Wait for the first keyframe; its payload carries SPS+PPS inline
        // (the host's H264 encoder prepends them to every IDR), so the
        // decoder can configure itself from the chunk data without a
        // separate description blob.
        if (!isKey) { framesDropped++; return; }
        const codec = extractCodecString(payload);
        // Explicit colorspace. libopenh264 (what the bundled LGPL ffmpeg
        // uses) does not write a full VUI into the SPS, so the stream
        // itself says "colorspace unspecified" and the decoder guesses -
        // which comes out as a visible hue shift because browsers at
        // >=720p default to BT.709 math while the encoder's swscale may
        // have used BT.601. Pinning this here tells the decoder to apply
        // BT.709 TV-range math regardless of what the SPS says, matching
        // what the host-side -vf chain actually produces.
        decoder.configure({
          codec,
          optimizeForLatency: true,
          colorSpace: {
            primaries: 'bt709',
            transfer: 'bt709',
            matrix: 'bt709',
            fullRange: false,
          },
        });
        configured = true;
      }
      // VideoDecoder timestamps are microseconds; captureTs is ms (epoch).
      // Make the value monotonically-ish increasing without overflowing a
      // safe integer by trimming the high bits.
      const tsUs = Number((header.captureTs % 0xFFFFFFFFn) * 1000n);
      decoder.decode(new EncodedVideoChunk({
        type: isKey ? 'key' : 'delta',
        timestamp: tsUs,
        data: payload,
      }));
    },
    stats(): H264DecodeStats {
      return { fps: fpsWindow.length, framesDecoded, framesDropped };
    },
    reset(): void {
      framesDecoded = 0;
      framesDropped = 0;
      fpsWindow = [];
      lastSeq = -1;
      configured = false;
      try { decoder.reset(); } catch { /* ignore */ }
    },
    close(): void {
      try { decoder.close(); } catch { /* ignore */ }
    },
  };
}
